import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

type Direction = "top" | "bottom" | "left" | "right";

interface DemoArrowAnnotationProps {
  targetSelector: string;
  text: string;
  direction?: Direction;
  className?: string;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const ACCENT = "#7C3AED";

/**
 * Animated SVG arrow that points at a target element matched via CSS selector,
 * with an attached caption. Recomputes position on resize/scroll.
 */
export default function DemoArrowAnnotation({
  targetSelector,
  text,
  direction = "bottom",
  className,
}: DemoArrowAnnotationProps) {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    const update = () => {
      const el = document.querySelector(targetSelector) as HTMLElement | null;
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    const el = document.querySelector(targetSelector) as HTMLElement | null;
    if (el) ro.observe(el);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const id = window.setInterval(update, 500);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      window.clearInterval(id);
    };
  }, [targetSelector]);

  if (!rect) return null;

  const GAP = 16;
  const ARROW_LEN = 64;

  // Anchor point on the target side closest to the arrow.
  let anchorX = rect.left + rect.width / 2;
  let anchorY = rect.top + rect.height / 2;
  let containerStyle: React.CSSProperties = {};
  let arrowSvg: React.ReactNode = null;
  let labelStyle: React.CSSProperties = {};

  switch (direction) {
    case "top":
      anchorY = rect.top - GAP;
      containerStyle = {
        top: anchorY - ARROW_LEN,
        left: anchorX,
        transform: "translateX(-50%)",
      };
      arrowSvg = (
        <svg width="40" height={ARROW_LEN} viewBox="0 0 40 64" fill="none">
          <path d="M20 4 V56" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
          <path d="M10 46 L20 60 L30 46" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
      labelStyle = { transform: "translate(-50%, -100%)", top: -8, left: "50%", position: "absolute" };
      break;
    case "bottom":
      anchorY = rect.top + rect.height + GAP;
      containerStyle = {
        top: anchorY,
        left: anchorX,
        transform: "translateX(-50%)",
      };
      arrowSvg = (
        <svg width="40" height={ARROW_LEN} viewBox="0 0 40 64" fill="none">
          <path d="M20 60 V8" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
          <path d="M10 18 L20 4 L30 18" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
      labelStyle = { transform: "translateX(-50%)", top: ARROW_LEN + 8, left: "50%", position: "absolute" };
      break;
    case "left":
      anchorX = rect.left - GAP;
      containerStyle = {
        top: anchorY,
        left: anchorX - ARROW_LEN,
        transform: "translateY(-50%)",
      };
      arrowSvg = (
        <svg width={ARROW_LEN} height="40" viewBox="0 0 64 40" fill="none">
          <path d="M4 20 H56" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
          <path d="M46 10 L60 20 L46 30" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
      labelStyle = { transform: "translate(-100%, -50%)", top: "50%", left: -8, position: "absolute" };
      break;
    case "right":
      anchorX = rect.left + rect.width + GAP;
      containerStyle = {
        top: anchorY,
        left: anchorX,
        transform: "translateY(-50%)",
      };
      arrowSvg = (
        <svg width={ARROW_LEN} height="40" viewBox="0 0 64 40" fill="none">
          <path d="M60 20 H8" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
          <path d="M18 10 L4 20 L18 30" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
      labelStyle = { transform: "translateY(-50%)", top: "50%", left: ARROW_LEN + 8, position: "absolute" };
      break;
  }

  return (
    <div
      className={cn("pointer-events-none fixed z-50 animate-fade-in", className)}
      style={containerStyle}
    >
      <div className="relative inline-block animate-pulse [animation-duration:2s]">
        {arrowSvg}
        <div
          style={labelStyle}
          className="whitespace-nowrap rounded-full bg-white/90 backdrop-blur border border-[#7C3AED]/30 px-3 py-1.5 text-xs font-medium text-navy shadow-md"
        >
          {text}
        </div>
      </div>
    </div>
  );
}
